import type { MutableRefObject } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LocalStorageKey } from "../../../constants/localStorageKeys";
import { RootState } from "../../../store/store";
import LayerPanel from "./layers/components/LayerPanel";
import MapControls from "./MapControls";
import { changeBasemap } from "./store/slices/mapSlice";
import { moveLayerDown, moveLayerUp, removeLayer, setOpacity, toggleVisible } from "./store/slices/layerSlice";
import type { MapType } from "./types/MapTypes";

/**
 * Cụm điều khiển của bản đồ (đổi lớp / nền, zoom, la bàn, thanh toạ độ) cho các bản đồ tự dựng bằng `ImagesMap`
 * (màn chi tiết / sửa đơn) — cùng giao diện và hành vi với cụm của `UniversalMap`. Chỉ maplibre có zoom / la bàn.
 */
export default function MapChromeControls({
  engineRef,
  mapType,
  showBaseMap = true,
  showMapControl = true,
}: {
  engineRef: MutableRefObject<any>;
  mapType: MapType;
  showBaseMap?: boolean;
  showMapControl?: boolean;
})
{
  const dispatch = useDispatch();
  const { layers } = useSelector((state: RootState) => state.layer);
  const { basemaps } = useSelector((state: RootState) => state.map);
  const { view: mapView, mousePosition } = useSelector((state: RootState) => state.mapBase);

  const onSetBasemap = (id: string) =>
  {
    const found = basemaps.find((b) => b.id === id);
    dispatch(changeBasemap(found));
    // Persist the selection so a reload restores this basemap — as UniversalMap does.
    if (found) localStorage.setItem(LocalStorageKey.BASEMAP_SELECTED, JSON.stringify(found));
  };

  const liveMap = () => (mapType === "maplibre" ? engineRef.current?.map : undefined);
  const onZoomIn = () => liveMap()?.setZoom(Math.min(mapView.zoom + 1, 22));
  const onZoomOut = () => liveMap()?.setZoom(Math.max(mapView.zoom - 1, 0));
  const onResetNorth = () =>
  {
    const map = liveMap();
    if (!map) return;
    // Về hướng bắc trên bản đồ THẬT, giữ tâm / zoom (redux mapView không theo dõi bearing).
    if (typeof map.resetNorthPitch === "function") map.resetNorthPitch({ duration: 300 });
    else map.easeTo({ bearing: 0, pitch: 0, duration: 300 });
  };

  return (
    <>
      {showBaseMap && (
        <LayerPanel
          layers={layers}
          onAdd={() => { }}
          onRemove={(id: string) => dispatch(removeLayer(id))}
          onToggle={(id: string) => dispatch(toggleVisible(id))}
          onOpacity={(id: string, opacity: number) => dispatch(setOpacity({ id, opacity }))}
          onMoveUp={(id: string) => dispatch(moveLayerUp(id))}
          onMoveDown={(id: string) => dispatch(moveLayerDown(id))}
          onSetBasemap={onSetBasemap}
        />
      )}
      {showMapControl && (
        <MapControls
          viewport={mapView}
          mousePosition={mousePosition}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onResetNorth={onResetNorth}
        />
      )}
    </>
  );
}
